import { ReportElement } from './report-element';
import { ReportUnits } from './report-units';
import { TemplateLine, ElementLocationInSection } from './report-template';
import { ElementType } from './element-type';
import { ReportPreviewMetadata, PropertyPreviewMetadata, FieldType, UpdateMetadata } from '../_services/report-template-design.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { LangUpdateService } from '../_services/lang-update.service';
import { BORDER_STYLE } from '../app.constants';

export class ReportLine extends ReportElement {
  public top: string;
  public left: string;
  public zIndex: number;
  public width: string;
  public height: string;
  public borderTopStyle: string;
  public borderTopColor: string;
  public borderLeftStyle: string;
  public borderLeftColor: string;
  public isCustomElementDraw?:boolean;

  private units: ReportUnits;

  private baseDef: TemplateLine;
  private readonly metaId = 'id';
  private readonly metaTop = 'y';
  private readonly metaLeft = 'x';
  private readonly metaWidth = 'width';
  private readonly metaHeight = 'height';
  private readonly metaZIndex = 'zIndex';
  private readonly metaBorderStyle = 'borderStyle';
  private readonly metaBorderColor = 'borderColor';
  private readonly metaLength = 'length';
  private readonly metaIsHorizontal = 'isHorizontal';

  constructor(index: number, units: ReportUnits, 
    protected translate: TranslateService,
    protected langService: LangUpdateService,
    templateLine?: TemplateLine) {

    super(ElementType.Line, index, !(templateLine == null), translate, langService, templateLine ? templateLine.id : null);

    this.units = units;

    if (templateLine) {
      this.baseDef = templateLine;
      this.baseDef.id = this.baseDef.id ? this.baseDef.id : this.generateUUID();
    } else {
      this.baseDef = {
        id: this.generateUUID(),
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        zIndex: 0,
        borderStyle: 'solid',
        borderColor: '#a0a0a0',
        length: 10,
        isHorizontal: true
      };
    }
    this.zIndex = this.baseDef.zIndex;

    this.top = this.units.concatenate('sizeUnit', this.baseDef.y);
    this.left = this.units.concatenate('sizeUnit', this.baseDef.x);

    this.width = this.getWidth();
    this.height = this.getHeight();
    this.borderTopStyle = this.getBorderTopStyle();
    this.borderTopColor = this.getBorderTopColor();
    this.borderLeftStyle = this.getBorderLeftStyle();
    this.borderLeftColor = this.getBorderLeftColor();

    this.setChainUpdate(); 
    this.setPreviewMetadata();
  }

  public setDrawData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });

    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    let lineLength: number;
    let lineIsHorizontal: boolean;
    if (metadata.widthPx < metadata.heightPx) {
      lineLength = metadata.heightPx;
      lineIsHorizontal = false;
    } else {
      lineLength = metadata.widthPx;
      lineIsHorizontal = true;
    }
    this.updateProperties({
      id: this.getId(),
      name: this.metaLength,
      value: this.units.fromPx('sizeUnit', lineLength),
      oldValue: this.baseDef[this.metaLength]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaIsHorizontal,
      value: lineIsHorizontal,
      oldValue: this.baseDef[this.metaIsHorizontal]
    });
  }

  public setDropData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });
  }

  public setResizeData(metadata: ReportPreviewMetadata) {
    this.updateProperties({
      id: this.getId(),
      name: this.metaTop,
      value: this.units.fromPx('sizeUnit', metadata.topPx),
      oldValue: this.baseDef[this.metaTop]
    });
    this.updateProperties({
      id: this.getId(),
      name: this.metaLeft,
      value: this.units.fromPx('sizeUnit', metadata.leftPx),
      oldValue: this.baseDef[this.metaLeft]
    });

    if (this.baseDef.isHorizontal) {
      this.updateProperties({
        id: this.getId(),
        name: this.metaLength,
        value: this.getValue(metadata.widthPx),
        oldValue: this.baseDef[this.metaLength]
      });
    } else {
      this.updateProperties({
        id: this.getId(),
        name: this.metaLength,
        value: this.getValue(metadata.heightPx),
        oldValue: this.baseDef[this.metaLength]
      });
    }
  }

  public getLeftPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaLeft]);
  }
  public getTopPx(): number {
    return this.units.toPx('sizeUnit', this.baseDef[this.metaTop]);
  }
  public getWidthPx(): number {
    if (this.getWidthNum() !== null && this.getWidthNum() < 0) {
      return this.units.toPx('sizeUnit', 1);
    } else {
      return this.units.toPx('sizeUnit', this.getWidthNum());
    }
  }
  public getHeightPx(): number {
    return this.units.toPx('sizeUnit', this.getHeightNum());
  }
  public getTemplateJSON(): TemplateLine {
    return this.baseDef;
  }

  protected getPreviewMetadata(): PropertyPreviewMetadata[] {
    return [
      {
        name: this.metaLeft, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaTop, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaZIndex, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Integer',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Position'
      },
      {
        name: this.metaBorderColor, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Color',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaBorderStyle, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Select',
        selectItems: BORDER_STYLE,
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaLength, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'Number',
        selectItems: [],
        isRequired: false,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
      {
        name: this.metaIsHorizontal, parentName: null, value: '',
        isFieldType: (type: FieldType): boolean => type === 'CheckBox',
        selectItems: [],
        isRequired: true,
        isEdited: false,
        isDisplay: true,
        group: 'Line'
      },
    ];
  }

  public setSectionInfo(id: string, stageName: string, location: ElementLocationInSection): void {
    this.baseDef.sectionId = id;
    this.baseDef.sectionStageTag = stageName;
    this.baseDef.elementLocationInSection = location;
  }

  protected setUpdateMetadata(metadata: UpdateMetadata) {
    switch (metadata.name) {
      case this.metaId:
        this.baseDef[metadata.name] = metadata.value as string;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaTop:
        this.baseDef[metadata.name] = metadata.value as number;
        this.top = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLeft:
        this.baseDef[metadata.name] = metadata.value as number;
        this.left = this.units.concatenate('sizeUnit', metadata.value as number);
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaWidth:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaHeight:
        this.baseDef[metadata.name] = metadata.value as number;
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaZIndex:
        this.baseDef[metadata.name] = metadata.value as number;
        this.zIndex = metadata.value as number;
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderStyle:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderTopStyle = this.getBorderTopStyle();
        this.borderLeftStyle = this.getBorderLeftStyle();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaBorderColor:
        this.baseDef[metadata.name] = metadata.value as string;
        this.borderTopColor = this.getBorderTopColor();
        this.borderLeftColor = this.getBorderLeftColor();
        this.setPreviewValue(metadata.name, metadata.value);
        break;
      case this.metaLength:
        this.baseDef[metadata.name] = metadata.value as number;
        this.width = this.getWidth();
        this.height = this.getHeight();
        this.setPreviewValue(metadata.name, metadata.value);
        this.setPreviewValue(this.metaWidth, this.getWidthNum());
        this.setPreviewValue(this.metaHeight, this.getHeightNum());
        break;
      case this.metaIsHorizontal:
        this.baseDef[metadata.name] = metadata.value as boolean;
        this.width = this.getWidth();
        this.height = this.getHeight();
        this.borderTopStyle = this.getBorderTopStyle();
        this.borderLeftStyle = this.getBorderLeftStyle();
        this.borderTopColor = this.getBorderTopColor();
        this.borderLeftColor = this.getBorderLeftColor();
        this.setPreviewValue(metadata.name, metadata.value);
        this.setPreviewValue(this.metaWidth, this.getWidthNum());
        this.setPreviewValue(this.metaHeight, this.getHeightNum());
        break;
    }
  }
  protected setChainUpdateFunction(subscribers: Array<Subscription>) {
    subscribers.push(
      this.units.onUpdateSizeUnit.subscribe((metadata: UpdateMetadata) => {
        this.updateProperties({
          id: this.getId(),
          name: this.metaTop,
          value: this.convertUnit(this.baseDef[this.metaTop], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaTop]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLeft,
          value: this.convertUnit(this.baseDef[this.metaLeft], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLeft]
        });
        this.updateProperties({
          id: this.getId(),
          name: this.metaLength,
          value: this.convertUnit(this.baseDef[this.metaLength], metadata.oldValue as string, metadata.value as string),
          oldValue: this.baseDef[this.metaLength]
        });
      })
    );
  }

  private getWidthNum(): number {
    let rtn: number;
    if (this.baseDef.isHorizontal) {
      rtn = this.baseDef.length;
    } else {
      rtn = 0;
    }
    return rtn;
  }
  private getHeightNum(): number {
    let rtn: number;
    if (this.baseDef.isHorizontal) {
      rtn = 0;
    } else {
      rtn = this.baseDef.length;
    }
    return rtn;
  }
  private getHeight(): string {
    return this.units.concatenate('sizeUnit', this.getHeightNum());
  }
  private getWidth(): string {
    if (
      this.getWidthNum() !== null &&
      this.getWidthNum() !== undefined &&
      this.getWidthNum() > 0
    ) {
      return this.units.concatenate('sizeUnit', this.getWidthNum());
    } else {
      return this.units.concatenate('sizeUnit', 1);
    }
  }

  private getBorderTopStyle(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = this.baseDef.borderStyle;
    } else {
      rtn = '';
    }
    return rtn;
  }
  private getBorderTopColor(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = this.baseDef.borderColor;
    } else {
      rtn = '';
    }
    return rtn;
  }
  private getBorderLeftStyle(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = '';
    } else {
      rtn = this.baseDef.borderStyle;
    }
    return rtn;
  }
  private getBorderLeftColor(): string {
    let rtn: string;
    if (this.baseDef.isHorizontal) {
      rtn = '';
    } else {
      rtn = this.baseDef.borderColor;
    }
    return rtn;
  }
  private getValue(value: number): number {
    if (value !== null && value > 0) {
      return this.units.fromPx('sizeUnit', value);
    } else {
      return this.units.fromPx('sizeUnit', 1);
    }
  }
  public isCustomElement() {
    return this.baseDef['isCustomElementDraw'];
   }
}
